import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ParamsPaginacion, SimpleResponse } from '../objetos/simple-response';
import { aplicarPaginacion } from './paginacion-params';
import { contextoSinSpinner } from '../../../shared/services/interceptors/peticion-sin-spinner';
import { Usuario } from '../objetos/usuario';
import { ConexionesService } from '../../../shared/services/conexiones.service';
import { environment } from '../../../../environments/environment';

import { BehaviorSubject } from 'rxjs';
import { Seleccionadas } from '../../../shared/objetos/seleccionada';
@Injectable({
  providedIn: 'root',
})
export class UsuariosService {
  private baseUrl = '';
  private avatarUrl = new BehaviorSubject<string>(
    localStorage.getItem('avatar') || 'default.jpg'
  );
  avatar$ = this.avatarUrl.asObservable();
  nombre$ = new BehaviorSubject<string>('');
  private readonly RUTA_EMPRESAS_SELECCIONADAS =
    '/api/usuarios/empresa-seleccionada';

  constructor(
    private http: HttpClient,
    private conexionesService: ConexionesService
  ) {
    //Subscripción al cambio de conexion
    conexionesService.conexionesRO.subscribe((res) => {
      if (res && res.length > 0) {
        const apiNombre = environment.conexiones_nombre.auth;
        const conexion: any = conexionesService.buscarConexion(apiNombre);
        this.baseUrl = conexion?.URL;
      }
    });
  }

  cambiarAvatar(nuevaRuta: string) {
    localStorage.setItem('avatar', nuevaRuta);
    this.avatarUrl.next(nuevaRuta);
  }

  obtenerUsuarios = (filtros?: {
    puestoId?: number | null;
    ubicacionId?: number | null;
    soloActivos?: boolean;
  }, paginacion?: ParamsPaginacion | null, silencioso = false): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/usuarios`;
    let params = new HttpParams();
    if (filtros?.puestoId) params = params.set('puestoId', filtros.puestoId);
    if (filtros?.ubicacionId) params = params.set('ubicacionId', filtros.ubicacionId);
    if (filtros?.soloActivos) params = params.set('activo', 'true');
    params = aplicarPaginacion(params, paginacion);
    return this.http
      .get(url, {
        params,
        context: silencioso ? contextoSinSpinner() : undefined,
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerUsuarioId = (usuario: Usuario): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/usuarios/${usuario.id}`;
    return this.http
      .get(url, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  crearUsusario = (usuario: Usuario): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/usuarios`;
    return this.http
      .post(url, usuario, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  editarUsusario = (usuario: Usuario): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/usuarios/${usuario.id}`;
    return this.http
      .put(url, usuario, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  editarUsuarioFoto = (usuario: FormData): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/usuarios/${usuario.get('id')}`;
    return this.http
      .put(url, usuario, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  editarContraseña = (usuario: Usuario): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/usuarios/${usuario.id}`;
    return this.http
      .patch(url, usuario, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  desactivarUsuario = (usuario: Usuario): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/usuarios/${usuario.id}`;
    return this.http
      .delete(url, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  asignacionPermisos = (
    id: number,
    tipoPermiso: number
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/puestos-opciones/obtener-todas-opciones/${id}`;
    return this.http
      .post(
        url,
        { tipoPermiso },
        {
          headers: {
            'x-auth': localStorage.getItem('token') || '',
          },
        }
      )
      .pipe(tap((resp: any) => resp));
  };

  /** Usuarios o puestos desde los que copiar permisos, con cuántas opciones del módulo tienen. */
  obtenerOrigenesCopia = (params: {
    tipo: 'usuario' | 'puesto';
    moduloId: number;
    busqueda?: string;
    pagina?: number;
    limite?: number;
    excluirId?: number | null;
  }): Observable<SimpleResponse> => {
    let query = new HttpParams().set('tipo', params.tipo).set('moduloId', params.moduloId);
    if (params.busqueda) query = query.set('busqueda', params.busqueda);
    if (params.pagina) query = query.set('pagina', params.pagina);
    if (params.limite) query = query.set('limite', params.limite);
    if (params.excluirId) query = query.set('excluirId', params.excluirId);
    return this.http
      .get(`${this.baseUrl}/api/puestos-opciones/origenes-copia`, {
        params: query,
        context: contextoSinSpinner(),
        headers: { 'x-auth': localStorage.getItem('token') || '' },
      })
      .pipe(tap((resp: any) => resp));
  };

  /** Opciones (menú + opción) que un usuario o puesto tiene efectivamente en un módulo. */
  obtenerPermisosOrigen = (
    tipo: 'usuario' | 'puesto',
    id: number,
    moduloId: number,
  ): Observable<SimpleResponse> => {
    return this.http
      .get(`${this.baseUrl}/api/puestos-opciones/permisos-origen/${tipo}/${id}`, {
        params: new HttpParams().set('moduloId', moduloId),
        headers: { 'x-auth': localStorage.getItem('token') || '' },
      })
      .pipe(tap((resp: any) => resp));
  };

  asignarPermisos = (
    datos: { id: number; opciones: any[]; padres: any[] },
    tipoPermiso: number
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/puestos-opciones/asignar-permisos`;
    return this.http
      .post(
        url,
        { datos, tipoPermiso },
        {
          headers: {
            'x-auth': localStorage.getItem('token') || '',
          },
        }
      )
      .pipe(tap((resp: any) => resp));
  };

  obtenerUsuariosPorIds = (usuarios: any): Observable<SimpleResponse> => {
    return this.http
      .post(`${this.baseUrl}/api/usuarios/porIds`, usuarios, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerUsuariosPorPuesto = (ids: any): Observable<SimpleResponse> => {
    return this.http
      .post(`${this.baseUrl}/api/usuarios/porPuestos`, ids, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  /**
   * Obtener las empresas seleccionadas del usuario, si no se proporciona el idUsuario, se obtiene las empresas seleccionadas de todos los usuarios
   * @param idUsuario - (OPCIONAL) ID del usuario
   * @returns
   */
  obtenerEmpresasSeleccionadas = (
    idUsuario?: number
  ): Observable<SimpleResponse> => {
    let getByUser: string = '';
    if (idUsuario) {
      getByUser = `/${idUsuario}`;
    }
    const url = `${this.baseUrl}${this.RUTA_EMPRESAS_SELECCIONADAS}${getByUser}`;
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .pipe(tap((resp: any) => resp));
  };

  /**
   * Registrar las empresas seleccionadas del usuario
   * @param seleccionadas - Seleccionadas
   * @returns
   */
  registrarEmpresasSeleccionadas = (
    seleccionadas: Seleccionadas
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}${this.RUTA_EMPRESAS_SELECCIONADAS}`;
    return this.http
      .post(url, seleccionadas, {
        headers: this.getHeaders(),
      })
      .pipe(tap((resp: any) => resp));
  };

  /**
   * Actualizar las empresas seleccionadas del usuario
   * @param seleccionadas - Seleccionadas
   * @returns
   */
  actualizarEmpresasSeleccionadas = (
    seleccionadas: Seleccionadas
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}${this.RUTA_EMPRESAS_SELECCIONADAS}`;
    return this.http
      .put(url, seleccionadas, {
        headers: this.getHeaders(),
      })
      .pipe(tap((resp: any) => resp));
  };

  /**
   * Eliminar las empresas seleccionadas del usuario
   * @param seleccionadas - Seleccionadas
   * @returns
   */
  eliminarEmpresasSeleccionadas = (
    seleccionadas: Seleccionadas
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}${this.RUTA_EMPRESAS_SELECCIONADAS}`;
    return this.http
      .delete(url, {
        body: seleccionadas,
        headers: this.getHeaders(),
      })
      .pipe(tap((resp: any) => resp));
  };

  /**
   * Obtener los headers de la petición
   * @returns HttpHeaders
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({ 'x-auth': localStorage.getItem('token') || '' });
  }
}
